/**
 * @file
 * Pure functions to normalize the raw API/field_shapes JSON tree
 * into the internal model used by the layout plan component.
 */

import type {
  ApiLayoutPlanNode,
  NormalizedPolygon,
  NormalizedChildLayer,
  NormalizedProperty,
  NormalizedRootLayer,
  Point,
} from './_types';

/**
 * Parse a single "x,y" coordinate string into a Point.
 */
function parsePointString(coord: string): Point | null {
  const [xStr, yStr] = coord.split(',');
  const x = Number(xStr);
  const y = Number(yStr);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

/**
 * Parse a polygon coordinate string ("x1,y1,x2,y2,...") into an array of Points.
 *
 * The API omits Coordinate on grouping nodes such as a "Project" root, which
 * the mapper turns into NULL, so an absent string yields no points.
 */
export function parseShapeCoordinates(shapeCoordinates: string | null | undefined): Point[] {
  if (!shapeCoordinates) return [];

  const tokens = shapeCoordinates.split(',');
  const points: Point[] = [];
  for (let i = 0; i < tokens.length - 1; i += 2) {
    const x = Number(tokens[i]);
    const y = Number(tokens[i + 1]);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      points.push({ x, y });
    }
  }
  return points;
}

/**
 * Parse a center coordinate string into a Point, or undefined if invalid.
 */
function parseCenter(centerCoordinate: string | null | undefined): Point | undefined {
  if (!centerCoordinate) return undefined;
  return parsePointString(centerCoordinate) ?? undefined;
}

/**
 * Recursively normalize a single API node into a NormalizedPolygon.
 */
function normalizeNode(node: ApiLayoutPlanNode): NormalizedPolygon {
  const property: NormalizedProperty | undefined = node.property
    ? {
        title: node.property.title,
        peb: node.property.peb,
        floor: node.property.floor,
        surface: node.property.surface,
        surfaceBalcony: node.property.surface_balcony,
        rooms: node.property.rooms,
        url: node.property.url,
        isSold: node.property.is_sold === true || node.property.is_sold === 1 || node.property.is_sold === '1',
      }
    : undefined;

  // Build child layers only when there are actual children and the node
  // is not a leaf property.
  //
  // Background image selection: a node's media_url is the image where that
  // node's own polygon coordinates are drawn. When drilling into a node to
  // show its children, the background should be the image where the
  // children's coordinates appear — i.e. the first child's media_url.
  const children = node.children ?? [];

  let childLayers: NormalizedChildLayer[] | undefined;
  if (!property && children.length > 0) {
    const childBg = children[0]?.media_url || node.media_url || null;

    childLayers = [
      {
        backgroundUrl: childBg,
        polygons: children.map(normalizeNode),
      },
    ];
  }

  return {
    id: String(node.id),
    points: parseShapeCoordinates(node.coordinate),
    levelType: node.type ?? '',
    propertyCode: node.label ?? '',
    name: node.label ?? '',
    status: '',
    // The mapper emits NULL when the API omits IsActive, which means "not
    // stated" rather than "inactive". Only an explicit false disables a shape,
    // matching how Project::getShapesArray() filters inactive roots.
    isActive: node.is_active !== false,
    center: parseCenter(node.center_coordinate),
    labelCoordinate: parseCenter(node.label_coordinate),
    property,
    childLayers,
  };
}

/**
 * Parse the raw data-shapes payload into a list of root nodes.
 *
 * field_shapes holds a JSON list, but a bare object is accepted as well so a
 * single-root payload written before the list migration still renders.
 */
export function parseLayoutPlanShapes(raw: string): ApiLayoutPlanNode[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  if (Array.isArray(parsed)) {
    return parsed.filter(isShapeNode);
  }

  return isShapeNode(parsed) ? [parsed] : [];
}

function isShapeNode(value: unknown): value is ApiLayoutPlanNode {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Normalize a single root node into its background image and polygons.
 *
 * When the root node has its own coordinates, it is presented as a single
 * clickable polygon on its own background image — drilling into it reveals
 * its children. When the root has no coordinates (e.g. a "Project" node),
 * its children are shown directly on the image their own coordinates are
 * drawn on, which is the first child's media_url.
 */
function normalizeRoot(root: ApiLayoutPlanNode): { backgroundUrl: string | null; polygons: NormalizedPolygon[] } {
  const children = root.children ?? [];
  const hasOwnCoordinates = (root.coordinate ?? '').trim().length > 0;

  if (hasOwnCoordinates) {
    return {
      backgroundUrl: root.media_url || null,
      polygons: [normalizeNode(root)],
    };
  }

  return {
    backgroundUrl: children[0]?.media_url || root.media_url || null,
    polygons: children.map(normalizeNode),
  };
}

/**
 * Normalize the list of root nodes into the top-level plans.
 *
 * A node's media_url is the image its own polygon coordinates are drawn on, so
 * roots resolving to the same background belong on the same canvas and are
 * merged into one layer. Roots on different backgrounds cannot share a canvas
 * and stay separate layers, which the component exposes as a plan switcher.
 * Roots that yield no polygon at all are dropped.
 */
export function normalizeLayoutPlanRoots(roots: ApiLayoutPlanNode[]): NormalizedRootLayer[] {
  const layers: NormalizedRootLayer[] = [];
  const layersByBackground = new Map<string, NormalizedRootLayer>();

  roots.forEach((root) => {
    const { backgroundUrl, polygons } = normalizeRoot(root);
    if (polygons.length === 0) return;

    const label = root.label ?? '';
    const existing = layersByBackground.get(backgroundUrl ?? '');

    if (existing) {
      existing.polygons.push(...polygons);
      if (label) {
        existing.label = existing.label ? `${existing.label}, ${label}` : label;
      }
      return;
    }

    const layer: NormalizedRootLayer = {
      label,
      backgroundUrl,
      polygons,
    };

    layersByBackground.set(backgroundUrl ?? '', layer);
    layers.push(layer);
  });

  return layers;
}
